import React, { useMemo } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { endOfMonth, startOfMonth } from "date-fns";
import { recordOverlapsLocalRange, utcToLocalDate, formatDurationMins } from "../utils/holidayDates";

function formatLocalDate(utcValue) {
  const localDate = utcToLocalDate(utcValue);
  if (!localDate) return "";
  return localDate.toLocaleDateString();
}

function formatType(record) {
  if (record.recordType === "publicHoliday") {
    return record.title
      ? `Public Holiday (${record.title})`
      : "Public Holiday";
  }
  if (record.recordType === "sickness") {
    return "Sickness";
  }
  if (record.recordType === "other") {
    return record.eventType ? `Other (${record.eventType})` : "Other";
  }
  return "Holiday";
}

function formatDuration(record) {
  if (record.recordType === "holiday") {
    if (record.durationType === "PartDay") {
      return record.partDayType || "Part Day";
    }
    return "Whole Day";
  }
  if (record.recordType !== "other" || record.durationMins == null) {
    return "";
  }
  return formatDurationMins(record.durationMins);
}

export default function HolidayListView({
  holidays,
  sicknesses = [],
  others = [],
  publicHolidays = [],
  month,
  getUser,
  isHrAdmin = false,
  onSelectHoliday,
}) {
  const classes = useStyles();

  const rows = useMemo(() => {
    const monthStart = startOfMonth(month);
    const monthEnd = endOfMonth(month);
    const combined = [
      ...(publicHolidays || []).map((p) => ({
        ...p,
        recordType: "publicHoliday",
      })),
      ...(holidays || []).map((h) => ({ ...h, recordType: "holiday" })),
      ...(sicknesses || []).map((s) => ({ ...s, recordType: "sickness" })),
      ...(others || []).map((o) => ({ ...o, recordType: "other" })),
    ].filter((record) => recordOverlapsLocalRange(record, monthStart, monthEnd));

    return combined.sort((a, b) => {
      const startA = utcToLocalDate(a.startDateTime);
      const startB = utcToLocalDate(b.startDateTime);
      const timeA = startA ? startA.getTime() : 0;
      const timeB = startB ? startB.getTime() : 0;
      return timeA - timeB;
    });
  }, [holidays, sicknesses, others, publicHolidays, month]);

  if (!rows.length) {
    return (
      <Box className={classes.empty}>
        <Typography variant="body1" color="textSecondary">
          No leave records in this period
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            {isHrAdmin ? (
              <TableCell className={classes.headerCell}>User</TableCell>
            ) : null}
            <TableCell className={classes.headerCell}>Start</TableCell>
            <TableCell className={classes.headerCell}>End</TableCell>
            <TableCell className={classes.headerCell}>Type</TableCell>
            <TableCell className={classes.headerCell}>Duration</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((record) => {
            const isPublicHoliday = record.recordType === "publicHoliday";
            const user = isPublicHoliday ? null : getUser(record.appUserId);
            const userName = isPublicHoliday
              ? "—"
              : user
                ? `${user.FirstName} ${user.LastName}`
                : `User ${record.appUserId}`;
            return (
              <TableRow
                key={`${record.recordType}-${record.id}`}
                hover={!isPublicHoliday}
                className={isPublicHoliday ? undefined : classes.row}
                onClick={
                  isPublicHoliday ? undefined : () => onSelectHoliday(record)
                }
              >
                {isHrAdmin ? (
                  <TableCell className={classes.bodyCell}>{userName}</TableCell>
                ) : null}
                <TableCell className={classes.bodyCell}>
                  {formatLocalDate(record.startDateTime)}
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  {formatLocalDate(record.endDateTime)}
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  {formatType(record)}
                </TableCell>
                <TableCell className={classes.bodyCell}>
                  {formatDuration(record)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    flexGrow: 1,
    border: `1px solid ${theme.palette.divider}`,
    maxHeight: "calc(100vh - 180px)",
  },
  headerCell: {
    fontWeight: 700,
    fontSize: "1.1rem",
    backgroundColor: theme.palette.background.paper,
  },
  bodyCell: {
    fontSize: "1.1rem",
  },
  row: {
    cursor: "pointer",
  },
  empty: {
    flexGrow: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 200,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
  },
}));
